<template>
  <div class="mentorship-page">

    <!-- ── Page Header ── -->
    <section class="page-header">
      <h1 class="page-title">Teaching &amp; Mentorship</h1>
      <p class="page-subtitle">
        As an educator, researcher, and practitioner, I believe in bridging theory and practice,
        shaping the next generation of designers who can navigate complex human-technology interactions.
      </p>
      <div class="stats-row">
        <div class="stat-item">
          <span class="stat-num">200+</span>
          <span class="stat-label">Students Mentored</span>
        </div>
        <div class="stat-item">
          <span class="stat-num">60+</span>
          <span class="stat-label">Projects</span>
        </div>
        <div class="stat-item">
          <span class="stat-num">15+</span>
          <span class="stat-label">Courses</span>
        </div>
        <div class="stat-item">
          <span class="stat-num">14+</span>
          <span class="stat-label">Years Teaching</span>
        </div>
      </div>
    </section>

    <!-- ── Courses I Teach ── -->
    <section class="courses-section">
      <div class="section-inner">
        <h2 class="section-title">Courses I Teach</h2>
        <div class="courses-grid">
          <div v-for="course in courses" :key="course.title" class="course-card">
            <div class="course-icon">{{ course.icon }}</div>
            <h4 class="course-title">{{ course.title }}</h4>
            <p class="course-desc">{{ course.desc }}</p>
            <div class="course-tags">
              <span v-for="tag in course.tags" :key="tag" class="course-tag">{{ tag }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Mentored Projects ── -->
    <section class="projects-section">
      <div class="section-inner">
        <h2 class="section-title">Mentored Projects</h2>
        <p class="section-desc">
          A selection of student projects I have mentored, spanning design research, emerging technology, and inclusive design.
        </p>
        <div class="projects-grid">
          <div
            v-for="project in projects"
            :key="project.id"
            class="project-card"
          >
            <div class="project-img-wrap">
              <img :src="project.image" :alt="project.title" class="project-img" />
            </div>
            <div class="project-body">
              <span class="project-category">{{ project.category }}</span>
              <h4 class="project-title">{{ project.title }}</h4>
              <p class="project-desc">{{ project.description }}</p>
              <!-- <button
                v-if="project.content"
                class="project-view-btn"
                @click="openModal(project.title, project.content, project.isImage, project.isPdf)"
              >View Case Study</button> -->
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Thesis Guided ── -->
    <section class="thesis-section">
      <div class="section-inner">
        <h2 class="section-title">Masters / Bachelors Graduation Projects Guided</h2>
        <p class="section-desc">
          Students who completed graduation internships at leading multinational companies under my mentorship.
        </p>
        <div class="thesis-grid">
          <div
            v-for="(thesis, idx) in thesisProjects"
            :key="idx"
            class="thesis-card"
          >
            <img :src="thesis.img" :alt="thesis.company" class="thesis-logo" />
            <div class="thesis-info">
              <div class="thesis-project">{{ thesis.projectTitle }}</div>
              <div class="thesis-student">{{ thesis.studentName }}</div>
              <div class="thesis-company">{{ thesis.company }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Teaching Philosophy ── -->
    <section class="philosophy-section">
      <div class="section-inner philosophy-inner">
        <div class="philosophy-text">
          <h2 class="section-title">Teaching Philosophy</h2>
          <p>
            I believe that great design education goes beyond tools and techniques. My teaching
            philosophy centers on <strong>learning by doing</strong>—every course I teach involves
            real-world projects where students solve genuine problems for actual users and organizations.
          </p>
          <p>
            I emphasize <strong>critical thinking and research</strong> as the foundation of design practice.
            Students learn to question assumptions, conduct rigorous user research, and back their design
            decisions with evidence rather than intuition alone.
          </p>
          <p>
            I foster an inclusive learning environment where every student's perspective is valued,
            preparing them to design for diverse users and contexts throughout their careers.
          </p>
        </div>
      </div>
    </section>

    <!-- ── Mentorship CTA ── -->
    <section class="cta-section">
      <div class="cta-inner">
        <h2>Looking for Mentorship?</h2>
        <p>
          I offer 1:1 mentorship for aspiring UX designers, design researchers, and students
          looking to build a strong foundation in human-centered design and technology.
        </p>
        <router-link :to="{ name: 'contact' }" class="cta-btn">
          Get in Touch &rarr;
        </router-link>
      </div>
    </section>

    <!-- ── Modal ── -->
    <BottomSheetModal
      :isVisible="isModalVisible"
      :title="modalTitle"
      :content="modalContent"
      :isImage="isContentImage"
      :isPdf="isContentPdf"
      @close="closeModal"
    />

  </div>
</template>

<script>
import BottomSheetModal from '@/components/BottomSheetModal.vue';

const imageMap = require.context('@/assets', false, /\.(png|jpe?g|gif|svg|webp)$/);
function assetUrl(filename) {
  try { return imageMap('./' + filename); } catch { return ''; }
}

export default {
  name: 'MentorshipPage',
  components: { BottomSheetModal },
  data() {
    return {
      isModalVisible: false,
      modalTitle: '',
      modalContent: '',
      isContentImage: false,
      isContentPdf: false,
      courses: [],
      projects: [],
      thesisProjects: [],
    };
  },
  methods: {
    openModal(title, content, isImage, isPdf) {
      this.modalTitle = title;
      this.modalContent = content;
      this.isContentImage = isImage;
      this.isContentPdf = isPdf;
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
    },
  },
  async mounted() {
    try {
      const url = process.env.BASE_URL + 'data/mentorship.json';
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${url}`);
      const data = await res.json();
      this.courses        = data.courses;
      this.projects       = data.projects.map(p => ({ ...p, image: assetUrl(p.image) }));
      this.thesisProjects = data.thesisProjects.map(t => ({ ...t, img: assetUrl(t.img) }));
    } catch (err) {
      console.error('mentorship.json load failed:', err);
    }
  },
};
</script>

<style scoped>
/* ── Page ── */
.mentorship-page {
  background: var(--color-bg-white);
  min-height: 100vh;
  overflow-x: hidden;
}

/* ── Header ── */
.page-header {
  text-align: center;
  padding: 7rem var(--space-6) var(--space-16);
  background: var(--color-bg-light);
  border-bottom: 1px solid var(--color-bg-subtle);
}
/* .page-title and .page-subtitle come from global style.css */
.page-subtitle { margin-bottom: var(--space-10); }
.stats-row {
  display: flex;
  justify-content: center;
  gap: var(--space-12);
  flex-wrap: wrap;
}
.stat-item { display: flex; flex-direction: column; align-items: center; }
.stat-num {
  font-size: var(--text-3xl);
  font-weight: var(--font-extrabold);
  color: var(--color-primary);
  line-height: 1;
}
.stat-label {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  font-weight: var(--font-medium);
  margin-top: 0.3rem;
}

/* ── Shared ── */
.section-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: var(--space-16) var(--space-6);
}
/* .section-title and .section-desc come from global style.css */

/* ── Courses ── */
.courses-section { background: var(--color-bg-white); }
.courses-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-6);
}
.course-card {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: var(--space-8);
  transition: box-shadow var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
}
.course-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-3px);
}
.course-icon { font-size: var(--text-2xl); margin-bottom: 0.75rem; }
.course-title {
  font-size: var(--text-base);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin-bottom: var(--space-2);
}
.course-desc {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-4);
}
.course-tags { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.course-tag {
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  color: var(--color-text-400);
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  padding: 0.2rem 0.65rem;
  
}

/* ── Mentored Projects ── */
.projects-section { background: var(--color-bg-light); }
.projects-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-6);
}
.project-card {
  background: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  overflow: hidden;
  transition: box-shadow var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
  display: flex;
  flex-direction: column;
}
.project-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-3px);
}
.project-img-wrap {
  height: 190px;
  overflow: hidden;
  background: var(--color-bg-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
}
.project-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform var(--duration-slow) var(--ease-out);
}
.project-card:hover .project-img { transform: scale(1.04); }
.project-body {
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  flex: 1;
}
.project-category {
  display: inline-block;
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  color: var(--color-primary);
  background: var(--color-primary-subtle);
  border-radius: var(--radius-full);
  padding: 0.2rem 0.65rem;
  margin-bottom: var(--space-2);
  width:fit-content !important;
}
.project-title {
  font-size: var(--text-sm);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin: 0 0 0.4rem;
}
.project-desc {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  line-height: var(--leading-relaxed);
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: var(--space-4);
}
.project-view-btn {
  background: var(--color-bg-white);
  color: var(--color-primary);
  border: 1.5px solid var(--color-primary);
  border-radius: var(--radius-full);
  padding: var(--space-2) var(--space-5);
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  cursor: pointer;
  transition: background var(--duration-base) var(--ease-out), color var(--duration-base) var(--ease-out);
  align-self: flex-start;
  margin-top: auto;
}
.project-view-btn:hover { background: var(--color-primary); color: var(--color-bg-white); }

/* ── Thesis ── */
.thesis-section { background: var(--color-bg-white); }
.thesis-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-5);
}
.thesis-card {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  background: var(--color-bg-white);
  border: 1px solid var(--color-bg-subtle);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
}
.thesis-logo {
  width: 64px;
  height: 64px;
  object-fit: contain;
  border-radius: var(--radius-md);
  flex-shrink: 0;
  background: var(--color-bg-white);
}
.thesis-project {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-bg-darker);
  line-height: var(--leading-snug);
  margin-bottom: 0.2rem;
}
.thesis-student {
  font-size: var(--text-sm);
  color: var(--color-text-400);
  margin-bottom: 0.1rem;
}
.thesis-company {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-primary);
}

/* ── Philosophy ── */
.philosophy-section { background: var(--color-bg-light); }
.philosophy-inner p {
  font-size: var(--text-sm);
  color: var(--color-text-500);
  line-height: var(--leading-loose);
  margin-bottom: var(--space-4);
}
.philosophy-inner p:last-child { margin-bottom: 0; }

/* ── CTA ── */
.cta-section {
  background: linear-gradient(135deg, var(--color-primary-subtle) 0%, var(--color-bg-white) 100%);
  padding: var(--section-py) var(--space-6);
  text-align: center;
}
.cta-inner { max-width: 600px; margin: 0 auto; }
.cta-inner h2 {
  font-size: var(--text-2xl);
  font-weight: var(--font-extrabold);
  color: var(--color-bg-darker);
  margin-bottom: var(--space-4);
}
.cta-inner p {
  font-size: var(--text-base);
  color: var(--color-text-400);
  line-height: var(--leading-loose);
  margin-bottom: var(--space-8);
}
.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  background: var(--color-primary);
  color: var(--color-bg-white);
  text-decoration: none;
  border-radius: var(--radius-full);
  padding: 0.9rem var(--space-10);
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  box-shadow: var(--shadow-orange);
  transition: background var(--duration-base) var(--ease-out), transform var(--duration-fast) var(--ease-out);
}
.cta-btn:hover { background: var(--color-primary-dark); transform: translateY(-2px); color: var(--color-bg-white); }

/* ── Responsive ── */
@media (max-width: 1024px) {
  .thesis-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 900px) {
  .courses-grid { grid-template-columns: 1fr; }
  .projects-grid { grid-template-columns: repeat(2, 1fr); }
  .thesis-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .page-header { padding: 6rem var(--space-4) var(--space-12); }
  .stats-row { gap: var(--space-6); }
  .section-inner { padding: var(--space-12) var(--space-4); }
  .projects-grid { grid-template-columns: 1fr; }
  .thesis-grid { grid-template-columns: 1fr; }
}
</style>
